// What our own edge (the Cloudflare Worker, /__ctx) knows about this visitor:
// country, region, city, timezone, EU flag, language and network kind, never
// the IP. Fetched once per browser session, kept in sessionStorage, signed by
// the edge so the pricing functions can trust the country.
export interface VisitorContext {
  country: string | null;
  region: string | null;
  city: string | null;
  timezone: string | null;
  continent: string | null;
  isEU: boolean | null;
  network: "hosting" | "relay" | "mobile" | "other" | "unknown";
  lang: string | null;
  issuedAt: number;
  sig?: string;
}

const KEY = "msth_ctx_v1";
let inflight: Promise<VisitorContext | null> | null = null;

/**
 * The pricing functions trust a signed context for 24 hours
 * (verifiedEdgeContext); a tab left open longer sent an expired one and got
 * group A prices (payments review, 2026-10-13). A saved context is used for
 * 12 hours, then fetched again; the margin covers a wrong device clock.
 */
export const CONTEXT_FRESH_MS = 12 * 60 * 60 * 1000;

export const cachedVisitorContext = (now: number = Date.now()): VisitorContext | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    const ctx = raw ? (JSON.parse(raw) as VisitorContext) : null;
    return ctx && typeof ctx.issuedAt === "number" && Math.abs(now - ctx.issuedAt) < CONTEXT_FRESH_MS ? ctx : null;
  } catch {
    return null;
  }
};

export function loadVisitorContext(): Promise<VisitorContext | null> {
  const cached = cachedVisitorContext();
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  if (typeof fetch === "undefined") return Promise.resolve(null);
  inflight = fetch("/__ctx", { cache: "no-store" })
    .then((r) => (r.ok && (r.headers.get("content-type") ?? "").includes("json") ? r.json() : null))
    .then((ctx: VisitorContext | null) => {
      if (ctx && typeof ctx.issuedAt === "number") {
        try { sessionStorage.setItem(KEY, JSON.stringify(ctx)); } catch { /* private mode */ }
        return ctx;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => { inflight = null; });
  return inflight;
}
